import { z } from "zod";
export const GELATO_COVER_TEMPLATE_EVIDENCE_CONTRACT = "GELATO_COVER_TEMPLATE_EVIDENCE_V1";
const safeIdSchema = z
    .string()
    .trim()
    .min(1)
    .max(500)
    .regex(/^[A-Za-z0-9][A-Za-z0-9_.:#/-]*$/u);
const sha256Schema = z.string().regex(/^[a-f0-9]{64}$/u);
const timestampSchema = z.string().datetime({ offset: true });
const immutableObjectSchema = z
    .object({
    objectKey: z
        .string()
        .trim()
        .min(1)
        .max(1_024)
        .refine((value) => !value.startsWith("/") && !value.includes("..")),
    versionId: z.string().trim().min(1).max(1_024),
    checksumSha256: sha256Schema,
    byteSize: z
        .number()
        .int()
        .positive()
        .max(100 * 1024 * 1024),
    contentType: z.enum(["application/json", "application/pdf"]),
})
    .strict();
const providerRegionSchema = z
    .object({
    leftMm: z.number().nonnegative().max(2_000),
    topMm: z.number().nonnegative().max(2_000),
    widthMm: z.number().positive().max(2_000),
    heightMm: z.number().positive().max(2_000),
    thicknessMm: z.number().nonnegative().max(100).nullable(),
})
    .strict();
const pdfRegionSchema = z
    .object({
    xMm: z.number().nonnegative().max(2_000),
    yMm: z.number().nonnegative().max(2_000),
    widthMm: z.number().positive().max(2_000),
    heightMm: z.number().positive().max(2_000),
})
    .strict();
const regionNames = [
    "wraparoundInside",
    "wraparoundEdge",
    "back",
    "jointBack",
    "spine",
    "jointFront",
    "front",
    "barcodeSafe",
];
const providerRegionsSchema = z.object(Object.fromEntries(regionNames.map((name) => [name, providerRegionSchema])));
const pdfRegionsSchema = z.object(Object.fromEntries(regionNames.map((name) => [name, pdfRegionSchema])));
export const gelatoCoverTemplateEvidenceV1Schema = z
    .object({
    contract: z.literal(GELATO_COVER_TEMPLATE_EVIDENCE_CONTRACT),
    evidenceId: safeIdSchema,
    profileId: safeIdSchema,
    profileVersion: safeIdSchema,
    productUid: safeIdSchema,
    requestedInteriorPageCount: z.number().int().positive().max(1_000),
    reportedCoverSurfaceCount: z.number().int().positive().max(1_004),
    measureUnit: z.literal("mm"),
    providerCoordinateOrigin: z.literal("TOP_LEFT"),
    pdfCoordinateOrigin: z.literal("BOTTOM_LEFT"),
    mediaSizeMm: z.object({ width: z.number().positive(), height: z.number().positive() }).strict(),
    providerRegions: providerRegionsSchema.strict(),
    pdfRegions: pdfRegionsSchema.strict(),
    request: immutableObjectSchema.extend({ contentType: z.literal("application/json") }),
    response: immutableObjectSchema.extend({ contentType: z.literal("application/json") }),
    template: immutableObjectSchema.extend({ contentType: z.literal("application/pdf") }),
    capturedAt: timestampSchema,
    reviewedAt: timestampSchema,
    reviewedBy: safeIdSchema,
    barcodeRegionSource: z.enum(["PROVIDER_CONFIRMED", "CONSERVATIVE_OPERATOR_ASSUMPTION"]),
    state: z.enum([
        "REVIEWED_EXACT_PRODUCT_TEMPLATE",
        "PROVISIONAL_EXACT_TEMPLATE_WITH_ASSUMED_BARCODE",
    ]),
    truthBoundary: z.enum([
        "REVIEWED_PROVIDER_TEMPLATE_EVIDENCE_NOT_PROFILE_ACTIVATION_OR_PHYSICAL_PROOF",
        "EXACT_PROVIDER_TEMPLATE_WITH_CONSERVATIVE_BARCODE_ASSUMPTION_NOT_PROVIDER_CONFIRMATION_ACTIVATION_OR_PHYSICAL_PROOF",
    ]),
})
    .strict()
    .superRefine((evidence, context) => {
    if ((evidence.barcodeRegionSource === "PROVIDER_CONFIRMED") !==
        (evidence.state === "REVIEWED_EXACT_PRODUCT_TEMPLATE"))
        context.addIssue({
            code: "custom",
            path: ["state"],
            message: "Template evidence state must disclose whether barcode geometry is provider-confirmed.",
        });
    if (evidence.reportedCoverSurfaceCount !== evidence.requestedInteriorPageCount + 4)
        context.addIssue({
            code: "custom",
            path: ["reportedCoverSurfaceCount"],
            message: "The reviewed cover response must report exactly N+4 physical surfaces.",
        });
    for (const name of regionNames) {
        const provider = evidence.providerRegions[name];
        const pdf = evidence.pdfRegions[name];
        const expectedY = evidence.mediaSizeMm.height - provider.topMm - provider.heightMm;
        if (Math.abs(pdf.xMm - provider.leftMm) > 0.001 ||
            Math.abs(pdf.yMm - expectedY) > 0.001 ||
            Math.abs(pdf.widthMm - provider.widthMm) > 0.001 ||
            Math.abs(pdf.heightMm - provider.heightMm) > 0.001)
            context.addIssue({
                code: "custom",
                path: ["pdfRegions", name],
                message: "PDF cover coordinates must be the exact top-left to bottom-left transform.",
            });
        if (pdf.xMm + pdf.widthMm > evidence.mediaSizeMm.width + 0.001 ||
            pdf.yMm + pdf.heightMm > evidence.mediaSizeMm.height + 0.001)
            context.addIssue({
                code: "custom",
                path: ["pdfRegions", name],
                message: "Every reviewed cover region must remain within the exact media box.",
            });
    }
    const { back, jointBack, spine, jointFront, front, barcodeSafe } = evidence.pdfRegions;
    if (back.xMm + back.widthMm > jointBack.xMm + 0.001 ||
        jointBack.xMm + jointBack.widthMm > spine.xMm + 0.001 ||
        spine.xMm + spine.widthMm > jointFront.xMm + 0.001 ||
        jointFront.xMm + jointFront.widthMm > front.xMm + 0.001)
        context.addIssue({
            code: "custom",
            path: ["pdfRegions"],
            message: "Back, joints, spine, and front regions must remain in provider order.",
        });
    if (barcodeSafe.xMm < back.xMm - 0.001 ||
        barcodeSafe.yMm < back.yMm - 0.001 ||
        barcodeSafe.xMm + barcodeSafe.widthMm > back.xMm + back.widthMm + 0.001 ||
        barcodeSafe.yMm + barcodeSafe.heightMm > back.yMm + back.heightMm + 0.001)
        context.addIssue({
            code: "custom",
            path: ["pdfRegions", "barcodeSafe"],
            message: "The exact barcode-safe region must be wholly inside the back cover.",
        });
});
//# sourceMappingURL=gelato-cover-template-evidence.js.map